import { LayoutTemplate } from "lucide-react";
import { editorColors as colors, fonts, MERGE_TAGS, CONTACT_TYPES } from "../../styles/theme";

export default function EditorSidebar({
  segment,
  setSegment,
  segmentCounts,
  audienceLists,
  customEmails,
  setCustomEmails,
  customEmailInput,
  setCustomEmailInput,
  title,
  setTitle,
  subject,
  setSubject,
  preheader,
  setPreheader,
  templates,
  selectedTemplate,
  setSelectedTemplate,
  insertMergeTag,
}) {
  return (
    <div
      style={{
        width: 260,
        flexShrink: 0,
        background: colors.bgCard,
        borderRight: `1px solid ${colors.border}`,
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
      }}
    >
      {/* Segment */}
      <div style={{ padding: 16, borderBottom: `1px solid ${colors.border}` }}>
        <label
          style={{
            fontSize: 10,
            color: colors.textSecondary,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontWeight: 700,
            display: "block",
            marginBottom: 6,
          }}
        >
          Send To
        </label>
        <select
          value={segment}
          onChange={(e) => setSegment(e.target.value)}
          style={{
            width: "100%",
            padding: "8px 10px",
            background: colors.bg,
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            color: colors.text,
            fontSize: 13,
            fontFamily: fonts.body,
            outline: "none",
          }}
        >
          <option value="test">Test Group ({segmentCounts.test})</option>
          <option value="custom">Custom Emails ({customEmails.length})</option>
          <option value="all" disabled>
            All Contacts ({segmentCounts.all})
          </option>
          {Object.entries(CONTACT_TYPES).map(([id, info]) => (
            <option key={id} value={id}>
              {info.label} ({segmentCounts[id] || "0"})
            </option>
          ))}
          <option value="digest">
            Monthly Digest ({segmentCounts.digest})
          </option>
          {audienceLists.length > 0 && (
            <optgroup label="Saved Audience Lists">
              {audienceLists.map((l) => (
                <option key={l.id} value={`list:${l.id}`}>
                  {l.name} ({segmentCounts[`list:${l.id}`] || "…"})
                </option>
              ))}
            </optgroup>
          )}
        </select>
        {segment === "custom" && (
          <div style={{ marginTop: 10 }}>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                type="email"
                value={customEmailInput}
                onChange={(e) => setCustomEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    const email = customEmailInput.trim().replace(/,$/,"").toLowerCase();
                    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !customEmails.includes(email)) {
                      setCustomEmails((prev) => [...prev, email]);
                    }
                    setCustomEmailInput("");
                  }
                }}
                placeholder="Type email and press Enter..."
                style={{
                  flex: 1,
                  padding: "7px 10px",
                  background: colors.bg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 6,
                  color: colors.text,
                  fontSize: 12,
                  fontFamily: fonts.body,
                  outline: "none",
                }}
              />
              <button
                onClick={() => {
                  const email = customEmailInput.trim().replace(/,$/,"").toLowerCase();
                  if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !customEmails.includes(email)) {
                    setCustomEmails((prev) => [...prev, email]);
                  }
                  setCustomEmailInput("");
                }}
                style={{
                  padding: "7px 12px",
                  background: colors.accent,
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: fonts.body,
                }}
              >
                Add
              </button>
            </div>
            {customEmails.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
                {customEmails.map((email) => (
                  <span
                    key={email}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "3px 8px",
                      background: `${colors.accent}18`,
                      border: `1px solid ${colors.accent}30`,
                      borderRadius: 4,
                      fontSize: 11,
                      color: colors.text,
                      fontFamily: fonts.body,
                    }}
                  >
                    {email}
                    <span
                      onClick={() => setCustomEmails((prev) => prev.filter((e) => e !== email))}
                      style={{ cursor: "pointer", color: colors.textSecondary, fontWeight: 700, fontSize: 13, lineHeight: 1 }}
                    >
                      &times;
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Title */}
      <div style={{ padding: 16, borderBottom: `1px solid ${colors.border}` }}>
        <label
          style={{
            fontSize: 10,
            color: colors.textSecondary,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontWeight: 700,
            display: "block",
            marginBottom: 6,
          }}
        >
          Newsletter Title
        </label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Internal name (e.g. March Weekly Digest)"
          style={{
            width: "100%",
            padding: "8px 10px",
            background: colors.bg,
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            color: colors.text,
            fontSize: 13,
            fontFamily: fonts.body,
            outline: "none",
          }}
        />
        <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 4 }}>
          For your reference only — recipients won't see this
        </div>
      </div>

      {/* Subject */}
      <div style={{ padding: 16, borderBottom: `1px solid ${colors.border}` }}>
        <label
          style={{
            fontSize: 10,
            color: colors.textSecondary,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontWeight: 700,
            display: "block",
            marginBottom: 6,
          }}
        >
          Subject Line
        </label>
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Enter subject line..."
          style={{
            width: "100%",
            padding: "8px 10px",
            background: colors.bg,
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            color: colors.text,
            fontSize: 13,
            fontFamily: fonts.body,
            outline: "none",
          }}
        />
        <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 6 }}>
          {subject.length}/60 chars
          {subject.length > 60 && (
            <span style={{ color: colors.warning }}> — may truncate</span>
          )}
          {subject === subject.toUpperCase() && subject.length > 5 && (
            <span style={{ color: colors.danger }}>
              {" "}— ALL CAPS triggers spam filters
            </span>
          )}
          {/[!]{2,}/.test(subject) && (
            <span style={{ color: colors.danger }}>
              {" "}— excessive punctuation
            </span>
          )}
        </div>
      </div>

      {/* Preheader */}
      <div style={{ padding: 16, borderBottom: `1px solid ${colors.border}` }}>
        <label
          style={{
            fontSize: 10,
            color: colors.textSecondary,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontWeight: 700,
            display: "block",
            marginBottom: 6,
          }}
        >
          Preview Text
        </label>
        <input
          value={preheader}
          onChange={(e) => setPreheader(e.target.value)}
          placeholder="Inbox preview snippet..."
          style={{
            width: "100%",
            padding: "8px 10px",
            background: colors.bg,
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            color: colors.text,
            fontSize: 13,
            fontFamily: fonts.body,
            outline: "none",
          }}
        />
        <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 6 }}>
          Shown next to subject in inbox. {preheader.length}/100 chars
        </div>
      </div>

      {/* Templates */}
      <div style={{ padding: 16, borderBottom: `1px solid ${colors.border}` }}>
        <label
          style={{
            fontSize: 10,
            color: colors.textSecondary,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontWeight: 700,
            display: "block",
            marginBottom: 10,
          }}
        >
          Templates
        </label>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {templates.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setSelectedTemplate(t);
                if (t.subject) setSubject(t.subject);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 10px",
                borderRadius: 6,
                border: `1px solid ${selectedTemplate?.id === t.id ? colors.accent : colors.border}`,
                background:
                  selectedTemplate?.id === t.id
                    ? colors.accentDim
                    : colors.bg,
                color:
                  selectedTemplate?.id === t.id
                    ? colors.accent
                    : colors.textSecondary,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: fonts.body,
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <LayoutTemplate size={14} />
              {t.name}
            </button>
          ))}
        </div>
      </div>

      {/* Merge Tags */}
      <div style={{ padding: 16 }}>
        <label
          style={{
            fontSize: 10,
            color: colors.textSecondary,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontWeight: 700,
            display: "block",
            marginBottom: 10,
          }}
        >
          Merge Tags
        </label>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {MERGE_TAGS.map((mt) => (
            <button
              key={mt.tag}
              onClick={() => insertMergeTag(mt.tag)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "6px 8px",
                borderRadius: 4,
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontSize: 11,
                fontFamily: fonts.body,
                color: colors.textSecondary,
                textAlign: "left",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = colors.bgHover)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              <span>{mt.label}</span>
              <code
                style={{
                  fontFamily: fonts.mono,
                  fontSize: 10,
                  color: colors.accent,
                }}
              >
                {mt.tag}
              </code>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
